import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { locationLabel } from '../data/locations';
import { Link } from 'react-router-dom';
import './Profile.css';

const VIDEO_INTRO_ENABLED = process.env.REACT_APP_VIDEO_INTRO_ENABLED === 'true';

function statusLabel(status, language) {
  const map = language === 'zh'
    ? { open: '招募中', in_progress: '进行中', completed: '已完成', closed: '已关闭' }
    : { open: 'Recruiting', in_progress: 'In progress', completed: 'Completed', closed: 'Closed' };
  return map[status] || status;
}

function StarRating({ value, language = 'zh' }) {
  const score = Math.round(Number(value || 0));
  return (
    <span className="profile-stars" aria-label={language === 'zh' ? `${score} 星` : `${score} stars`}>
      {'★'.repeat(score)}{'☆'.repeat(Math.max(0, 5 - score))}
    </span>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const { language, translatePhrase } = useLanguage();
  const text = language === 'zh' ? {
    loading: '加载中...',
    availabilityFull: '每周 30 小时以上',
    availabilityPart: '每周 10-20 小时',
    verifyNotice: '身份验证申请已记录，平台会进入资质审核流程',
    verifiedPending: '身份审核中',
    verify: '验证身份',
    identityVerified: '身份已认证',
    identityUnverified: '身份未认证',
    identityVerificationPaused: '用户自助身份验证暂未接入，请由平台管理员在后台认证',
    location: value => `◎ ${value || '未设置地区'}`,
    role: '技能服务者',
    reviewScore: (rating, total) => `${rating} 分 · ${total} 条评价`,
    noReviewsShort: '暂无评价',
    records: count => `${count} 个需求`,
    publicView: '查看公开视图',
    settings: '资料设置',
    video: '视频介绍',
    videoNotice: '视频介绍已加入资料完善清单，后续可在资料设置中上传',
    weekly: '每周可工作时间',
    availabilityNotice: '可工作时间已更新',
    availabilityHint: '可在接单前继续调整',
    skills: '技能',
    defaultTitleReact: '全栈开发与自动化工程师',
    defaultTitleAlgo: '算法工程师',
    defaultBio: '我擅长把复杂需求拆解成清晰的技术方案，覆盖数据分析、自动化工具、Web 应用开发和交付落地。合作过程中会主动同步进度、确认验收标准，并优先保证交付质量和沟通效率。',
    editTitle: '编辑档案',
    bio: '个人简介',
    bioPlaceholder: '介绍你的能力、经验和适合承接的项目...',
    skillsInput: '技能，用逗号分隔',
    skillsPlaceholder: 'React, Python, UI 设计',
    saving: '保存中...',
    save: '保存资料',
    cancel: '取消',
    portfolio: '我的服务',
    portfolioAria: '发布服务作品',
    allServices: '全部',
    published: '已发布',
    pendingServices: '待审核',
    drafts: '草稿',
    rejectedServices: '已拒绝',
    addProject: '添加一个项目',
    portfolioHint: '发布服务或案例后，客户能更快判断你是否适合他们的需求。',
    editService: '编辑',
    deleteService: '删除',
    deleteConfirm: '确定删除这个服务吗？删除后不可恢复。',
    confirmDelete: '确认删除',
    cancelDelete: '取消',
    servicePending: '待审核',
    servicePublished: '已发布',
    serviceRejected: '已拒绝',
    serviceDraft: '草稿',
    servicesLoading: '正在加载我的服务...',
    servicesLoadError: '我的服务加载失败，请刷新重试',
    noServices: '还没有发布服务。',
    noFilteredServices: '当前分类下没有服务。',
    viewAllServices: '管理全部服务',
    recentRequirements: '最近发布的需求',
    viewAllRequirements: '查看全部需求',
    noRequirementTitle: '还没有创建任何需求',
    noRequirementText: '发布需求或完成协作后，记录会展示在这里。',
    reviews: '评价',
    noReviewTitle: '暂无评价',
    noReviewText: '完成订单后，客户评价会沉淀为你的信任资产。',
    noComment: '无评论',
    loadFallback: '正在显示当前账号资料快照，稍后会自动获取最新资料',
  } : {
    loading: 'Loading...',
    availabilityFull: 'More than 30 hrs/week',
    availabilityPart: '10-20 hrs/week',
    verifyNotice: 'Identity verification request recorded. The platform will review your qualifications.',
    verifiedPending: 'Verification pending',
    verify: 'Verify identity',
    identityVerified: 'Identity verified',
    identityUnverified: 'Identity not verified',
    identityVerificationPaused: 'Self-service identity verification is not connected yet. Platform admins can verify users in the admin panel.',
    location: value => `◎ ${value || 'Location not set'}`,
    role: 'Skill provider',
    reviewScore: (rating, total) => `${rating} rating · ${total} reviews`,
    noReviewsShort: 'No reviews yet',
    records: count => `${count} requirements`,
    publicView: 'See public view',
    settings: 'Profile settings',
    video: 'Video introduction',
    videoNotice: 'Video introduction has been added to your profile checklist. Upload it later in settings.',
    videoPaused: 'Video introduction upload is not connected yet',
    weekly: 'Hours per week',
    availabilityNotice: 'Availability updated',
    availabilityHint: 'You can adjust this before accepting work',
    skills: 'Skills',
    defaultTitleReact: 'Full-stack development and automation engineer',
    defaultTitleAlgo: 'Algorithm engineer',
    defaultBio: 'I break complex requirements into clear technical plans, covering data analysis, automation tools, web app development, and delivery. I keep progress visible, confirm acceptance criteria, and prioritize delivery quality and communication.',
    editTitle: 'Edit profile',
    bio: 'Bio',
    bioPlaceholder: 'Introduce your capabilities, experience, and suitable projects...',
    skillsInput: 'Skills, separated by commas',
    skillsPlaceholder: 'React, Python, UI design',
    saving: 'Saving...',
    save: 'Save profile',
    cancel: 'Cancel',
    portfolio: 'My services',
    portfolioAria: 'Post service work',
    allServices: 'All',
    published: 'Published',
    pendingServices: 'Pending review',
    drafts: 'Drafts',
    rejectedServices: 'Rejected',
    addProject: 'Add a project',
    portfolioHint: 'After publishing services or cases, clients can judge fit faster.',
    editService: 'Edit',
    deleteService: 'Delete',
    deleteConfirm: 'Delete this service? This cannot be undone.',
    confirmDelete: 'Confirm delete',
    cancelDelete: 'Cancel',
    servicePending: 'Pending review',
    servicePublished: 'Published',
    serviceRejected: 'Rejected',
    serviceDraft: 'Draft',
    servicesLoading: 'Loading your services...',
    servicesLoadError: 'Your services could not be loaded. Refresh and try again.',
    noServices: 'No services published yet.',
    noFilteredServices: 'No services in this category.',
    viewAllServices: 'Manage all services',
    recentRequirements: 'Recently posted requirements',
    viewAllRequirements: 'View all requirements',
    noRequirementTitle: 'No requirements posted yet',
    noRequirementText: 'Posted requirements or completed collaborations will appear here.',
    reviews: 'Reviews',
    noReviewTitle: 'No reviews yet',
    noReviewText: 'After orders are completed, client reviews become your trust assets.',
    noComment: 'No comment',
    loadFallback: 'Showing your current profile snapshot. Latest profile details will refresh automatically.',
  };
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ bio: '', skills: '' });
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], avg_rating: null, total: 0 });
  const [ownedServices, setOwnedServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesLoadError, setServicesLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [availability, setAvailability] = useState(text.availabilityFull);
  const videoPaused = text.videoPaused || text.videoNotice;
  const identityVerificationPaused = text.identityVerificationPaused || text.verifyNotice;

  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  };

  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const fallbackProfile = {
      ...user,
      skills: Array.isArray(user.skills) ? user.skills : [],
      bio: user.bio || '',
    };

    api.get('/users/me')
      .then(res => {
        if (!mounted) return;
        setProfile(res.data);
        setForm({ bio: res.data.bio || '', skills: (res.data.skills || []).join(', ') });
      })
      .catch(() => {
        if (!mounted) return;
        setProfile(fallbackProfile);
        setForm({ bio: fallbackProfile.bio || '', skills: (fallbackProfile.skills || []).join(', ') });
        showNotice(text.loadFallback);
      });

    api.get('/tasks/mine')
      .then(res => {
        if (!mounted) return;
        setMyTasks(res.data.tasks || []);
      })
      .catch(() => {
        if (mounted) setMyTasks([]);
      });

    api.get(`/reviews/user/${user.id}`)
      .then(res => {
        if (mounted) setReviews(res.data);
      })
      .catch(() => {
        if (mounted) setReviews({ reviews: [], avg_rating: null, total: 0 });
      });

    setServicesLoading(true);
    setServicesLoadError('');
    api.get('/marketplace/services/mine')
      .then(res => {
        if (mounted) setOwnedServices(res.data.services || []);
      })
      .catch(() => {
        if (!mounted) return;
        setOwnedServices([]);
        setServicesLoadError(text.servicesLoadError);
      })
      .finally(() => {
        if (mounted) setServicesLoading(false);
      });

    return () => { mounted = false; };
  }, [user, text.loadFallback, text.servicesLoadError]);

  useEffect(() => {
    setAvailability(text.availabilityFull);
  }, [language, text.availabilityFull]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const skills = form.skills.split(',').map(s => s.trim()).filter(Boolean);
      await api.put('/users/me', { bio: form.bio, skills });
      const res = await api.get('/users/me');
      setProfile(res.data);
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="loading">{text.loading}</div>;

  const skills = profile.skills?.length ? profile.skills : ['React', '算法建模', '数据分析'];
  const displayName = profile.username || 'Bai L.';
  const title = skills.includes('React') ? text.defaultTitleReact : text.defaultTitleAlgo;
  const bio = profile.bio ? translatePhrase(profile.bio) : text.defaultBio;
  const isIdentityVerified = profile.verificationStatus === 'verified';
  const rawLocation = [profile.country, profile.city, profile.district].filter(Boolean).join(':');
  const displayLocation = rawLocation
    ? (locationLabel(rawLocation, language) || [profile.country, profile.city, profile.district].filter(Boolean).join(' · '))
    : '';

  return (
    <div className="profile-page">
      <div className="profile-shell">
        <section className="profile-hero-panel">
          <div className="profile-hero-main">
            <div className="profile-avatar-wrap">
              <span className="profile-online-dot" />
              <div className="profile-avatar">
                {profile.avatar ? <img src={profile.avatar} alt="" /> : displayName[0]?.toUpperCase()}
              </div>
              <button className="profile-avatar-edit" type="button" onClick={() => setEditMode(true)}>✎</button>
            </div>
            <div className="profile-identity">
              <div className="profile-name-row">
                <h1>{displayName}</h1>
                <span
                  className={`profile-verified ${isIdentityVerified ? '' : 'profile-verified-muted'}`}
                  aria-label={isIdentityVerified ? text.identityVerified : text.identityUnverified}
                  title={isIdentityVerified ? text.identityVerified : text.identityUnverified}
                >
                  ✓
                </span>
                {isIdentityVerified ? (
                  <span className="profile-verification-label">{text.identityVerified}</span>
                ) : (
                  <span className="profile-verification-paused">{identityVerificationPaused}</span>
                )}
              </div>
              <p className="profile-location">{text.location(displayLocation)}</p>
              <div className="profile-meta-line">
                <span>{text.role}</span>
                <span>{reviews.avg_rating ? text.reviewScore(reviews.avg_rating, reviews.total) : text.noReviewsShort}</span>
                <span>{text.records(myTasks.length)}</span>
              </div>
            </div>
          </div>
          <div className="profile-actions">
            <Link to="/freelancers" className="profile-btn profile-btn-outline">{text.publicView}</Link>
            <Link to="/settings" className="profile-btn profile-btn-primary">{text.settings}</Link>
          </div>
        </section>

        {notice && <div className="profile-toast">{notice}</div>}

        <div className="profile-body-grid">
          <aside className="profile-sidebar">
            <div className="profile-side-list">
              {VIDEO_INTRO_ENABLED && (
                <div>
                  <h3>{text.video}</h3>
                  <button type="button" onClick={() => showNotice(videoPaused)}>＋</button>
                </div>
              )}
              <div>
                <h3>{text.weekly}</h3>
                <button type="button" onClick={() => {
                  setAvailability(value => value === text.availabilityFull ? text.availabilityPart : text.availabilityFull);
                  showNotice(text.availabilityNotice);
                }}>✎</button>
                <p>{availability}<br />{text.availabilityHint}</p>
              </div>
              <div>
                <h3>{text.skills}</h3>
                <button type="button" onClick={() => setEditMode(true)}>✎</button>
                <div className="profile-skill-list">
                  {skills.map(skill => <span key={skill}>{translatePhrase(skill)}</span>)}
                </div>
              </div>
            </div>

          </aside>

          <main className="profile-main">
            <section className="profile-section profile-summary-section">
              <div className="profile-title-row">
                <h2>{title}</h2>
                <button type="button" onClick={() => setEditMode(true)}>✎</button>
              </div>
              <p className="profile-summary">{bio}</p>
              <div className="profile-tags">
                {skills.map(skill => <span key={skill}>{translatePhrase(skill)}</span>)}
              </div>
            </section>

            {editMode && (
              <section className="profile-section profile-edit-panel">
                <h2>{text.editTitle}</h2>
                <label>
                  {text.bio}
                  <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={5} placeholder={text.bioPlaceholder} />
                </label>
                <label>
                  {text.skillsInput}
                  <input value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} placeholder={text.skillsPlaceholder} />
                </label>
                <div className="profile-edit-actions">
                  <button className="profile-btn profile-btn-primary" type="button" disabled={saving} onClick={handleSave}>{saving ? text.saving : text.save}</button>
                  <button className="profile-btn profile-btn-outline" type="button" onClick={() => setEditMode(false)}>{text.cancel}</button>
                </div>
              </section>
            )}

            <section className="profile-section">
              <div className="profile-section-head">
                <h2>{text.portfolio}</h2>
                <Link to="/services/mine">{text.viewAllServices}</Link>
              </div>
              {servicesLoading ? (
                <div className="profile-service-state">{text.servicesLoading}</div>
              ) : servicesLoadError ? (
                <div className="profile-service-state profile-service-error">{servicesLoadError}</div>
              ) : ownedServices.length === 0 ? (
                <div className="portfolio-empty">
                  <p>{text.noServices}</p>
                  <Link to="/services/new">{text.addProject}</Link>
                </div>
              ) : (
                <div className="profile-service-list">
                  {ownedServices.slice(0, 3).map(service => {
                    const statusText = service.status === 'published'
                      ? text.servicePublished
                      : service.status === 'rejected'
                        ? text.serviceRejected
                        : service.status === 'draft'
                          ? text.serviceDraft
                          : text.servicePending;
                    const content = (
                      <div>
                        <strong>{translatePhrase(service.title)}</strong>
                        <span>{translatePhrase(service.category)} · {service.price || '-'} · {statusText}</span>
                      </div>
                    );
                    return service.status === 'published' ? (
                      <Link to={`/services/listing-${service.id}`} className="profile-work-row profile-service-row" key={service.id}>
                        {content}
                      </Link>
                    ) : (
                      <div className="profile-work-row profile-service-row" key={service.id}>
                        {content}
                      </div>
                    );
                  })}
                  {ownedServices.length > 3 && (
                    <div className="profile-empty-line">
                      <span>{language === 'zh' ? `还有 ${ownedServices.length - 3} 个服务` : `${ownedServices.length - 3} more services`}</span>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <h2>{text.recentRequirements}</h2>
                <Link to="/requirements/mine">{text.viewAllRequirements}</Link>
              </div>
              {myTasks.length === 0 ? (
                <div className="profile-empty-line">
                  <strong>{text.noRequirementTitle}</strong>
                  <span>{text.noRequirementText}</span>
                </div>
              ) : (
                myTasks.map(task => (
                  <Link to={`/tasks/${task.id}`} className="profile-work-row" key={task.id}>
                    <div>
                      <strong>{translatePhrase(task.title)}</strong>
                      <span>{translatePhrase(task.category)} · {statusLabel(task.status, language)}</span>
                    </div>
                    <b>¥{task.budget_min}-{task.budget_max}</b>
                  </Link>
                ))
              )}
            </section>

            <section className="profile-section">
              <div className="profile-section-head">
                <h2>{text.reviews}</h2>
                {reviews.avg_rating && <span><StarRating value={reviews.avg_rating} language={language} /> {reviews.avg_rating}</span>}
              </div>
              {reviews.reviews.length === 0 ? (
                <div className="profile-empty-line">
                  <strong>{text.noReviewTitle}</strong>
                  <span>{text.noReviewText}</span>
                </div>
              ) : (
                reviews.reviews.map(review => (
                  <div className="profile-review-row" key={review.id}>
                    <div><StarRating value={review.rating} language={language} /><span>{review.reviewer_name} · {new Date(review.created_at).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US')}</span></div>
                    <p>{translatePhrase(review.comment) || text.noComment}</p>
                  </div>
                ))
              )}
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
